import React, { useState, useMemo, useCallback } from 'react';
import {
  getForecastMonths15,
  getPriorMonth,
  mockBulkEditData,
  BulkEditMockRow,
  SUPPLIER_OPTIONS,
  CONTRACT_TYPE_OPTIONS,
  RAMPING_OPTIONS,
  STRATEGY_CODE_OPTIONS,
  SELLING_CATEGORY_OPTIONS,
} from './bulkEditMockData';

/* ------------------------------------------------------------------ */
/*  Inline CSS-module-like styles (plain objects, no PrimeReact)       */
/* ------------------------------------------------------------------ */

const colors = {
  primary: '#4d2e6d',
  primaryLight: '#7c5aa4',
  white: '#ffffff',
  grey: '#e5e7eb',
  greyLight: '#f3f4f6',
  greyDark: '#6b7280',
  teal: '#028383',
  red: '#d8000c',
  green: '#059669',
  greenBg: '#d1fae5',
  redBg: '#fee2e2',
  expandedBg: '#f8f9fa',
  rowAlt: '#F7F3FE',
  bannerBg: '#fef3c7',
  bannerBorder: '#f59e0b',
  priorCycleBg: '#eef2ff',
  masBlue: '#2563eb',
  masBg: '#dbeafe',
  ncOrange: '#d97706',
  ncBg: '#fef3c7',
};

const s = {
  /* ---- layout ---- */
  container: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSize: '13px',
    color: '#1f2937',
  } as React.CSSProperties,

  /* ---- info banner ---- */
  banner: {
    margin: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: colors.bannerBg,
    border: `1px solid ${colors.bannerBorder}`,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '13px',
  } as React.CSSProperties,
  badge: {
    backgroundColor: colors.bannerBorder,
    color: colors.white,
    padding: '2px 8px',
    borderRadius: '4px',
    fontWeight: 700,
    fontSize: '11px',
    letterSpacing: '0.5px',
  } as React.CSSProperties,

  /* ---- filter section ---- */
  filterContainer: {
    border: `1px solid ${colors.grey}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    margin: '1rem',
    padding: '1rem',
  } as React.CSSProperties,
  filterRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
    marginBottom: '0.75rem',
  } as React.CSSProperties,
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  } as React.CSSProperties,
  filterLabel: {
    fontWeight: 500,
    fontSize: '13px',
    color: '#374151',
  } as React.CSSProperties,
  filterInput: {
    height: '2rem',
    borderRadius: '4px',
    border: `1px solid ${colors.grey}`,
    padding: '0 0.5rem',
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  } as React.CSSProperties,
  filterSelect: {
    height: '2rem',
    borderRadius: '4px',
    border: `1px solid ${colors.grey}`,
    padding: '0 0.5rem',
    fontSize: '13px',
    backgroundColor: colors.white,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  } as React.CSSProperties,
  filterBtnRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '0.5rem',
  } as React.CSSProperties,

  /* ---- grid section ---- */
  gridWrapper: {
    border: `1px solid ${colors.grey}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    margin: '1rem',
    height: '64vh',
    overflowY: 'auto',
    overflowX: 'auto',
  } as React.CSSProperties,
  table: {
    /* table-layout:fixed + pixel widths ensure sticky left offsets stay in sync.
       border-collapse:separate (with spacing:0) is required — 'collapse' breaks
       position:sticky in all major browsers (cells lose their background, causing
       white flicker on scroll). */
    tableLayout: 'fixed',
    minWidth: '2308px',   /* 672px fixed + 15×96px months + 196px actions */
    borderCollapse: 'separate',
    borderSpacing: 0,
  } as React.CSSProperties,
  thead: {
    backgroundColor: colors.primaryLight,
  } as React.CSSProperties,
  th: {
    color: colors.white,
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '100%',
    padding: '0.5rem 0.35rem',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: colors.primaryLight,
    borderBottom: '2px solid rgba(255,255,255,0.2)',
  } as React.CSSProperties,
  td: {
    textAlign: 'center',
    padding: '0.35rem',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #e5e7eb',
  } as React.CSSProperties,

  /* ---- buttons ---- */
  btn: {
    height: '2rem',
    padding: '0 1rem',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
  } as React.CSSProperties,
  primaryBtn: {
    backgroundColor: colors.primaryLight,
    color: colors.white,
  } as React.CSSProperties,
  clearBtn: {
    backgroundColor: colors.rowAlt,
    color: colors.primary,
    border: `1px solid ${colors.greyDark}`,
  } as React.CSSProperties,
  tealBtn: {
    backgroundColor: colors.teal,
    color: colors.white,
  } as React.CSSProperties,
  redBtn: {
    backgroundColor: colors.red,
    color: colors.white,
  } as React.CSSProperties,
  smallBtn: {
    height: '1.6rem',
    padding: '0 0.6rem',
    fontSize: '11px',
    borderRadius: '3px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
  } as React.CSSProperties,

  /* ---- toggle switch ---- */
  toggleTrack: (checked: boolean) =>
    ({
      width: '36px',
      height: '18px',
      borderRadius: '9px',
      backgroundColor: checked ? colors.green : colors.greyDark,
      position: 'relative',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      display: 'inline-block',
      verticalAlign: 'middle',
    }) as React.CSSProperties,
  toggleThumb: (checked: boolean) =>
    ({
      width: '14px',
      height: '14px',
      borderRadius: '50%',
      backgroundColor: colors.white,
      position: 'absolute',
      top: '2px',
      left: checked ? '20px' : '2px',
      transition: 'left 0.2s',
    }) as React.CSSProperties,

  /* ---- badges ---- */
  masBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: colors.masBg,
    color: colors.masBlue,
  } as React.CSSProperties,
  ncBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: colors.ncBg,
    color: colors.ncOrange,
  } as React.CSSProperties,

  /* ---- cell input ---- */
  cellInput: {
    width: '5.5rem',
    fontSize: '12px',
    height: '1.4rem',
    border: `1px solid ${colors.grey}`,
    borderRadius: '3px',
    textAlign: 'right',
    padding: '0 0.3rem',
    outline: 'none',
  } as React.CSSProperties,

  /* ---- copy dot ---- */
  copyDot: {
    position: 'absolute',
    bottom: '4px',
    right: '6px',
    cursor: 'pointer',
    fontSize: '22px',
    lineHeight: '10px',
    color: colors.primaryLight,
    fontWeight: 'bold',
    userSelect: 'none',
  } as React.CSSProperties,

  /* ---- expanded row ---- */
  expandedLabel: {
    padding: '0.4rem 0.5rem',
    fontSize: '11px',
    fontWeight: 700,
    color: '#374151',
  } as React.CSSProperties,
  expandedValue: {
    padding: '0.4rem 0.3rem',
    fontSize: '11px',
    textAlign: 'center',
    color: '#4b5563',
  } as React.CSSProperties,
  priorLabel: {
    padding: '0.4rem 0.5rem',
    fontSize: '11px',
    fontWeight: 700,
    color: colors.primaryLight,
  } as React.CSSProperties,
  priorValue: {
    padding: '0.4rem 0.3rem',
    fontSize: '11px',
    textAlign: 'center',
    fontWeight: 600,
    color: colors.primaryLight,
  } as React.CSSProperties,

  /* ---- bottom bar ---- */
  bottomBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 1rem 1rem',
  } as React.CSSProperties,
} as const;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Filters {
  ramping: string;
  molecule: string;
  sellingCategory: string;
  supplierName: string;
  concept: string;
  strategyCode: string;
  ndc11: string;
  contractType: string;
}

const emptyFilters: Filters = {
  ramping: '',
  molecule: '',
  sellingCategory: '',
  supplierName: '',
  concept: '',
  strategyCode: '',
  ndc11: '',
  contractType: '',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const BulkEditUpdated: React.FC = () => {
  /* ---- static data ---- */
  const forecastMonths = useMemo(() => getForecastMonths15(), []);
  const priorMonth = useMemo(() => getPriorMonth(), []);

  /* ---- state ---- */
  const [filters, setFilters] = useState<Filters>({ ...emptyFilters });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({ ...emptyFilters });
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [editValues, setEditValues] = useState<Map<string, Map<string, string>>>(() => {
    const map = new Map<string, Map<string, string>>();
    mockBulkEditData.forEach(row => {
      const rowMap = new Map<string, string>();
      row.forecast_data.forEach(fd => {
        rowMap.set(fd.ForecastPeriod, String(fd.AdjForecast));
      });
      map.set(row.NDC11, rowMap);
    });
    return map;
  });
  const [conceptSort, setConceptSort] = useState<'' | 'asc' | 'desc'>('');
  const [modelModes, setModelModes] = useState<Map<string, boolean>>(() => {
    const m = new Map<string, boolean>();
    mockBulkEditData.forEach(r => m.set(r.NDC11, r.isModelMode));
    return m;
  });
  const [focusedCell, setFocusedCell] = useState<string | null>(null);
  const [commentOpen, setCommentOpen] = useState<string | null>(null);
  const [commentText, setCommentText] = useState<string>('');

  /* ---- derived: filtered + sorted data ---- */
  const filteredData: BulkEditMockRow[] = useMemo(() => {
    let result = [...mockBulkEditData];

    const f = appliedFilters;

    // Text filters (case-insensitive substring)
    if (f.concept) {
      const lc = f.concept.toLowerCase();
      result = result.filter(r => r.Concept.toLowerCase().includes(lc));
    }
    if (f.ndc11) {
      const lc = f.ndc11.toLowerCase();
      result = result.filter(r => r.NDC11.toLowerCase().includes(lc));
    }
    if (f.molecule) {
      const lc = f.molecule.toLowerCase();
      result = result.filter(r => r.MoleculeAndDosageForm.toLowerCase().includes(lc));
    }

    // Dropdown exact match
    if (f.ramping) {
      const boolVal = f.ramping === 'true';
      result = result.filter(r => r.Ramping === boolVal);
    }
    if (f.sellingCategory) {
      result = result.filter(r => r.SellingCategory === f.sellingCategory);
    }
    if (f.strategyCode) {
      result = result.filter(r => r.StrategyCode === f.strategyCode);
    }
    if (f.supplierName) {
      result = result.filter(r => r.SupplierName === f.supplierName);
    }
    if (f.contractType) {
      result = result.filter(r => r.ContractType === f.contractType);
    }

    // Sorting
    if (conceptSort === 'asc') {
      result.sort((a, b) => a.Concept.localeCompare(b.Concept));
    } else if (conceptSort === 'desc') {
      result.sort((a, b) => b.Concept.localeCompare(a.Concept));
    }

    return result;
  }, [appliedFilters, conceptSort]);

  /* ---- handlers ---- */
  const handleFilterChange = useCallback(
    (field: keyof Filters, value: string) => {
      setFilters(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters({ ...filters });
    setExpandedRows(new Set());
  }, [filters]);

  const handleClear = useCallback(() => {
    setFilters({ ...emptyFilters });
    setAppliedFilters({ ...emptyFilters });
    setExpandedRows(new Set());
  }, []);

  const toggleExpand = useCallback((idx: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const cycleSort = useCallback(() => {
    setConceptSort(prev => {
      if (prev === '') return 'asc';
      if (prev === 'asc') return 'desc';
      return '';
    });
  }, []);

  const getSortArrow = (): string => {
    if (conceptSort === 'asc') return ' \u25B2';
    if (conceptSort === 'desc') return ' \u25BC';
    return ' \u25C6';
  };

  const handleCellChange = useCallback(
    (ndc: string, dateStr: string, value: string) => {
      const stripped = value.replace(/,/g, '');
      setEditValues(prev => {
        const next = new Map(prev);
        const rowMap = new Map(next.get(ndc) || new Map());
        rowMap.set(dateStr, stripped);
        next.set(ndc, rowMap);
        return next;
      });
    },
    [],
  );

  const getCellValue = (ndc: string, dateStr: string): string => {
    const raw = editValues.get(ndc)?.get(dateStr) ?? '';
    if (raw === '') return '';
    const num = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(num)) return raw;
    return num.toLocaleString('en-US');
  };

  const handleCopyForward = useCallback(
    (ndc: string, fromDateStr: string) => {
      const value = getCellValue(ndc, fromDateStr);
      const startIdx = forecastMonths.findIndex(m => m.dateString === fromDateStr);
      if (startIdx < 0) return;
      setEditValues(prev => {
        const next = new Map(prev);
        const rowMap = new Map(next.get(ndc) || new Map());
        for (let i = startIdx + 1; i < forecastMonths.length; i++) {
          rowMap.set(forecastMonths[i].dateString, value);
        }
        next.set(ndc, rowMap);
        return next;
      });
      setFocusedCell(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [forecastMonths, editValues],
  );

  const toggleModel = useCallback((ndc: string) => {
    setModelModes(prev => {
      const next = new Map(prev);
      next.set(ndc, !next.get(ndc));
      return next;
    });
  }, []);

  const getIsModel = (ndc: string): boolean => {
    return modelModes.get(ndc) ?? true;
  };

  const handleSaveRow = useCallback((_ndc: string) => {
    // MOCKUP: no-op
    alert(`[MOCKUP] Save triggered for NDC ${_ndc}`);
  }, []);

  const handleSaveAll = useCallback(() => {
    alert('[MOCKUP] Save All triggered');
  }, []);

  const handleComment = useCallback((ndc: string) => {
    setCommentOpen(prev => (prev === ndc ? null : ndc));
    setCommentText('');
  }, []);

  const handleCommentSubmit = useCallback(
    (ndc: string) => {
      alert(`[MOCKUP] Comment for ${ndc}: "${commentText}"`);
      setCommentOpen(null);
      setCommentText('');
    },
    [commentText],
  );

  /* ---- render helpers ---- */
  const renderContractBadge = (ct: 'MAS' | 'NC') => (
    <span style={ct === 'MAS' ? s.masBadge : s.ncBadge}>{ct}</span>
  );

  const renderToggle = (ndc: string) => {
    const checked = getIsModel(ndc);
    return (
      <span
        style={s.toggleTrack(checked)}
        onClick={() => toggleModel(ndc)}
        title={checked ? 'Model (click to switch to Manual)' : 'Manual (click to switch to Model)'}
      >
        <span style={s.toggleThumb(checked)} />
      </span>
    );
  };

  const renderStatus = (ndc: string) => {
    const isModel = getIsModel(ndc);
    return (
      <span
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: isModel ? colors.green : colors.red,
          backgroundColor: isModel ? colors.greenBg : colors.redBg,
          padding: '2px 8px',
          borderRadius: '4px',
        }}
      >
        {isModel ? 'Model' : 'Manual'}
      </span>
    );
  };

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */

  return (
    <div style={s.container}>
      {/* ---- Info Banner ---- */}
      <div style={s.banner}>
        <span style={s.badge}>MOCKUP</span>
        <span>
          Unconstrained Forecast Bulk Edit (15-Month). Features: Supplier &amp; Contract Type filters,
          Model/Manual toggle, Prior cycle visibility, 15-month forecast with sticky fixed columns.
        </span>
      </div>

      {/* ================================================================ */}
      {/*  FILTER SECTION                                                   */}
      {/* ================================================================ */}
      <div style={s.filterContainer}>
        {/* Row 1 */}
        <div style={s.filterRow}>
          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Ramping:</label>
            <select
              style={s.filterSelect}
              value={filters.ramping}
              onChange={e => handleFilterChange('ramping', e.target.value)}
            >
              {RAMPING_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Molecule:</label>
            <input
              type="text"
              placeholder="Search molecule..."
              style={s.filterInput}
              value={filters.molecule}
              onChange={e => handleFilterChange('molecule', e.target.value)}
            />
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Selling Category:</label>
            <select
              style={s.filterSelect}
              value={filters.sellingCategory}
              onChange={e => handleFilterChange('sellingCategory', e.target.value)}
            >
              {SELLING_CATEGORY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Supplier Name:</label>
            <select
              style={s.filterSelect}
              value={filters.supplierName}
              onChange={e => handleFilterChange('supplierName', e.target.value)}
            >
              {SUPPLIER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2 */}
        <div style={s.filterRow}>
          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Concept:</label>
            <input
              type="text"
              placeholder="Search concept..."
              style={s.filterInput}
              value={filters.concept}
              onChange={e => handleFilterChange('concept', e.target.value)}
            />
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Strategy Code:</label>
            <select
              style={s.filterSelect}
              value={filters.strategyCode}
              onChange={e => handleFilterChange('strategyCode', e.target.value)}
            >
              {STRATEGY_CODE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>NDC:</label>
            <input
              type="text"
              placeholder="Search NDC..."
              style={s.filterInput}
              value={filters.ndc11}
              onChange={e => handleFilterChange('ndc11', e.target.value)}
            />
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Contract Type:</label>
            <select
              style={s.filterSelect}
              value={filters.contractType}
              onChange={e => handleFilterChange('contractType', e.target.value)}
            >
              {CONTRACT_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Button row */}
        <div style={s.filterBtnRow}>
          <button
            style={{ ...s.btn, ...s.clearBtn }}
            onClick={handleClear}
          >
            Clear Filter
          </button>
          <button
            style={{ ...s.btn, ...s.primaryBtn }}
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/*  GRID SECTION                                                     */}
      {/* ================================================================ */}
      <div style={s.gridWrapper}>
        <table style={s.table}>
          <thead style={s.thead}>
            <tr>
              {/* Expand — sticky left: 0, width: 40px */}
              <th style={{ ...s.th, width: '40px', left: 0, zIndex: 11, borderTopLeftRadius: '8px' }}></th>
              {/* Concept — sticky left: 40px, width: 176px */}
              <th
                style={{ ...s.th, width: '176px', left: '40px', zIndex: 11, cursor: 'pointer', userSelect: 'none', boxShadow: `-4px 0 0 0 ${colors.primaryLight}` }}
                onClick={cycleSort}
                title="Click to sort by Concept"
              >
                Concept{getSortArrow()}
              </th>
              {/* NDC11 — sticky left: 215px, width: 128px */}
              <th style={{ ...s.th, width: '128px', left: '215px', zIndex: 11, boxShadow: `-4px 0 0 0 ${colors.primaryLight}` }}>NDC11</th>
              {/* Supplier — sticky left: 343px, width: 96px */}
              <th style={{ ...s.th, width: '96px', left: '343px', zIndex: 11, boxShadow: `-4px 0 0 0 ${colors.primaryLight}` }}>Supplier</th>
              {/* Contract Type — sticky left: 439px, width: 80px */}
              <th style={{ ...s.th, width: '80px', left: '439px', zIndex: 11, boxShadow: `-4px 0 0 0 ${colors.primaryLight}` }}>Contract Type</th>
              {/* Mode — sticky left: 519px, width: 72px */}
              <th style={{ ...s.th, width: '72px', left: '519px', zIndex: 11, boxShadow: `-4px 0 0 0 ${colors.primaryLight}` }}>Mode</th>
              {/* Status — sticky left: 591px — right scroll boundary shadow */}
              <th style={{ ...s.th, width: '80px', left: '591px', zIndex: 11, boxShadow: `-4px 0 0 0 ${colors.primaryLight}, 3px 0 6px -2px rgba(0,0,0,0.25)` }}>Status</th>
              {/* 15 month columns — each 96px wide */}
              {forecastMonths.map((m) => (
                <th key={m.dateString} style={{ ...s.th, width: '96px' }}>
                  {m.label}
                </th>
              ))}
              {/* Actions / Save All — sticky right: 0, width: 196px */}
              <th
                style={{
                  ...s.th,
                  width: '196px',
                  position: 'sticky',
                  right: 0,
                  zIndex: 11,
                  backgroundColor: colors.white,
                  color: colors.primary,
                  borderTopRightRadius: '8px',
                  padding: '0.35rem 0.5rem',
                  boxShadow: '-3px 0 6px -2px rgba(0,0,0,0.25)',
                }}
              >
                <button
                  style={{ ...s.btn, ...s.tealBtn, width: '100%' }}
                  onClick={handleSaveAll}
                >
                  &#10003; Save All
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 && (
              <tr>
                <td
                  colSpan={8 + forecastMonths.length}
                  style={{ padding: '3rem', textAlign: 'center', color: colors.greyDark }}
                >
                  No Unconstrained Forecast data available for the applied filter values.
                </td>
              </tr>
            )}

            {filteredData.map((row, idx) => {
              const isExpanded = expandedRows.has(idx);
              const bgColor = idx % 2 ? colors.rowAlt : colors.white;

              return (
                <React.Fragment key={`${row.NDC11}-${idx}`}>
                  {/* ---- Main Row ---- */}
                  <tr style={{ backgroundColor: bgColor, height: '3.5rem' }}>
                    {/* Expand — sticky left: 0 */}
                    <td style={{ ...s.td, width: '40px', position: 'sticky', left: 0, zIndex: 2, backgroundColor: bgColor }}>
                      <span
                        style={{ cursor: 'pointer', fontSize: '14px', userSelect: 'none' }}
                        onClick={() => toggleExpand(idx)}
                        title={isExpanded ? 'Collapse' : 'Expand to see prior cycle'}
                      >
                        {isExpanded ? '\u25BC' : '\u25B6'}
                      </span>
                    </td>
                    {/* Concept — sticky left: 40px */}
                    <td style={{ ...s.td, width: '176px', textAlign: 'left', paddingLeft: '0.5rem', fontSize: '11px', position: 'sticky', left: '40px', zIndex: 2, backgroundColor: bgColor, boxShadow: `-4px 0 0 0 ${bgColor}` }}>
                      {row.Concept}
                    </td>
                    {/* NDC11 — sticky left: 215px */}
                    <td style={{ ...s.td, width: '128px', fontSize: '11px', position: 'sticky', left: '215px', zIndex: 2, backgroundColor: bgColor, boxShadow: `-4px 0 0 0 ${bgColor}` }}>{row.NDC11}</td>
                    {/* Supplier — sticky left: 343px */}
                    <td style={{ ...s.td, width: '96px', fontSize: '11px', position: 'sticky', left: '343px', zIndex: 2, backgroundColor: bgColor, boxShadow: `-4px 0 0 0 ${bgColor}` }}>{row.SupplierName}</td>
                    {/* Contract Type — sticky left: 439px */}
                    <td style={{ ...s.td, width: '80px', position: 'sticky', left: '439px', zIndex: 2, backgroundColor: bgColor, boxShadow: `-4px 0 0 0 ${bgColor}` }}>{renderContractBadge(row.ContractType)}</td>
                    {/* Mode — sticky left: 519px */}
                    <td style={{ ...s.td, width: '72px', position: 'sticky', left: '519px', zIndex: 2, backgroundColor: bgColor, boxShadow: `-4px 0 0 0 ${bgColor}` }}>{renderToggle(row.NDC11)}</td>
                    {/* Status — sticky left: 591px */}
                    <td style={{ ...s.td, width: '80px', position: 'sticky', left: '591px', zIndex: 2, backgroundColor: bgColor, boxShadow: `-4px 0 0 0 ${bgColor}, 3px 0 6px -2px rgba(0,0,0,0.15)` }}>{renderStatus(row.NDC11)}</td>
                    {/* 15 month cells */}
                    {forecastMonths.map((m, mIdx) => {
                      const cellKey = `${row.NDC11}-${idx}-${m.dateString}`;
                      const isFocused = focusedCell === cellKey;
                      return (
                        <td key={m.dateString} style={{ ...s.td, position: 'relative' }}>
                          <input
                            type="text"
                            style={s.cellInput as React.CSSProperties}
                            value={getCellValue(row.NDC11, m.dateString)}
                            onFocus={() => setFocusedCell(cellKey)}
                            onBlur={() => {
                              // Delay blur to allow copy-dot click
                              setTimeout(() => {
                                setFocusedCell(prev => (prev === cellKey ? null : prev));
                              }, 150);
                            }}
                            onChange={e => handleCellChange(row.NDC11, m.dateString, e.target.value)}
                          />
                          {isFocused && mIdx < forecastMonths.length - 1 && (
                            <span
                              title="Copy value to all cells to the right"
                              style={s.copyDot as React.CSSProperties}
                              onMouseDown={e => {
                                e.preventDefault();
                                handleCopyForward(row.NDC11, m.dateString);
                              }}
                            >
                              &#9679;
                            </span>
                          )}
                        </td>
                      );
                    })}
                    {/* Actions — sticky right: 0 */}
                    <td style={{ ...s.td, width: '196px', position: 'sticky', right: 0, zIndex: 2, backgroundColor: bgColor, boxShadow: '-3px 0 6px -2px rgba(0,0,0,0.1)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center' }}>
                        <button
                          style={{ ...s.smallBtn, backgroundColor: colors.teal, color: colors.white }}
                          onClick={() => handleSaveRow(row.NDC11)}
                        >
                          &#128190; Save
                        </button>
                        <button
                          style={{ ...s.smallBtn, backgroundColor: colors.primaryLight, color: colors.white }}
                          onClick={() => handleComment(row.NDC11)}
                        >
                          &#128172;
                        </button>
                      </div>
                      {/* Inline comment popover */}
                      {commentOpen === row.NDC11 && (
                        <div
                          style={{
                            position: 'absolute',
                            right: '1rem',
                            marginTop: '0.25rem',
                            zIndex: 20,
                            backgroundColor: colors.white,
                            border: `1px solid ${colors.grey}`,
                            borderRadius: '6px',
                            padding: '0.75rem',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                            width: '220px',
                          }}
                        >
                          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '0.4rem' }}>
                            Add Comment ({row.NDC11})
                          </div>
                          <textarea
                            rows={3}
                            style={{
                              width: '100%',
                              fontSize: '12px',
                              border: `1px solid ${colors.grey}`,
                              borderRadius: '4px',
                              padding: '0.3rem',
                              resize: 'none',
                              boxSizing: 'border-box',
                            }}
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            placeholder="Enter comment..."
                          />
                          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', justifyContent: 'flex-end' }}>
                            <button
                              style={{ ...s.smallBtn, ...s.clearBtn, fontSize: '10px' }}
                              onClick={() => setCommentOpen(null)}
                            >
                              Cancel
                            </button>
                            <button
                              style={{ ...s.smallBtn, backgroundColor: colors.teal, color: colors.white, fontSize: '10px' }}
                              onClick={() => handleCommentSubmit(row.NDC11)}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* ---- Expanded Row: Prior Month / Previous Cycle ---- */}
                  {isExpanded && (
                    <tr style={{ backgroundColor: colors.priorCycleBg, borderTop: `1px solid ${colors.grey}` }}>
                      <td style={{ ...s.td, width: '40px', position: 'sticky', left: 0, zIndex: 2, backgroundColor: colors.priorCycleBg }}></td>
                      <td
                        colSpan={6}
                        style={{
                          ...s.priorLabel as React.CSSProperties,
                          position: 'sticky',
                          left: '40px',
                          zIndex: 2,
                          backgroundColor: colors.priorCycleBg,
                          boxShadow: `3px 0 6px -2px rgba(0,0,0,0.15)`,
                        }}
                      >
                        Prior Cycle ({priorMonth.label})
                      </td>
                      {forecastMonths.map((_m, mIdx) => (
                        <td key={`prior-${_m.dateString}`} style={s.priorValue as React.CSSProperties}>
                          {row.priorCycleForecast && row.priorCycleForecast[mIdx] != null
                            ? row.priorCycleForecast[mIdx].toLocaleString()
                            : '\u2014'}
                        </td>
                      ))}
                      <td></td>
                    </tr>
                  )}

                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---- Bottom Bar ---- */}
      <div style={s.bottomBar}>
        <button
          style={{ ...s.btn, ...s.redBtn }}
          onClick={() => alert('[MOCKUP] Cancel clicked')}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default BulkEditUpdated;
